/** Une valeur de cellule : les nombres restent des nombres, le reste est du texte. */
export type CsvValue = number | string

/**
 * Le BOM UTF-8 en tête de fichier : sans lui, Excel ouvre le CSV en Windows-1252
 * et les accents des noms d'apprenants arrivent illisibles.
 */
export const CSV_BOM = '\uFEFF'

/**
 * Un texte qui commence comme une formule (`=`, `+`, `-`, `@`, tabulation,
 * retour) est préfixé d'une apostrophe : le tableur l'affiche tel quel au lieu
 * de l'exécuter. Les exports portent du texte saisi ailleurs — une réponse
 * libre d'apprenant, un intitulé de module — qu'un `=HYPERLINK(…)` suffirait à
 * transformer en piège pour l'admin qui ouvre le fichier. Les nombres, eux,
 * restent des nombres.
 */
export function neutralizeFormula(text: string): string {
  // Un « - » isolé (valeur vide de certains exports) n'est pas une formule.
  return /^(?:[=+\-@].|[\t\r])/s.test(text) ? `'${text}` : text
}

/**
 * Une cellule telle que le tableur la relit : entre guillemets (doublés) dès
 * qu'elle contient le séparateur, un guillemet ou un saut de ligne. Sans cela,
 * une réponse libre « Paris; France » décalait toutes les colonnes suivantes.
 */
export function csvCell(value: CsvValue): string {
  const text = typeof value === 'string' ? neutralizeFormula(value) : String(value)
  return /[;"\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

/** Le texte du fichier CSV, séparé par des points-virgules (Excel en français). */
export function csvContent(headers: string[], data: CsvValue[][]): string {
  return [headers, ...data].map(row => row.map(csvCell).join(';')).join('\n')
}

/** Le fichier complet, BOM compris, tel qu'un serveur l'envoie ou le joint à un mail. */
export function csvDocument(headers: string[], data: CsvValue[][]): string {
  return CSV_BOM + csvContent(headers, data)
}
